var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect, useCallback } from 'react';
import { ReactFlow, Controls, Background, useNodesState, useEdgesState, addEdge, MarkerType, ReactFlowProvider, PanOnScrollMode, } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Button } from '../../UI/Button';
import { Modal } from '../../UI/Modal';
import { useTheme } from '../../../lib/useTheme';
import { generateQuiz, generateFlashcards } from '../../../services/apiClient';
import { useToast } from '../../UI/Toast';
import { useAuth } from '../../../context/AuthContext';
import useProgressTracking from '../../../hooks/useProgressTracking';
import { Brain, } from 'lucide-react';
// Import our custom components
import { MindMapNode } from './MindMapNodes';
import { MindMapEdge } from './MindMapEdges';
import MindMapControls from './MindMapControls';
// React Flow Node Types
var nodeTypes = {
    mindMapNode: MindMapNode,
};
// React Flow Edge Types
var edgeTypes = {
    mindMapEdge: MindMapEdge,
};
// Hierarchical layout algorithm - optimized for best practices and clear hierarchy
var applyHierarchicalLayout = function (nodes, center, spreadFactor) {
    console.log('Applying hierarchical layout with center:', center, 'spreadFactor:', spreadFactor, 'nodes:', nodes.length);
    var levels = {};
    nodes.forEach(function (n) {
        var lvl = typeof n.level === 'number' ? n.level : 0;
        if (!levels[lvl])
            levels[lvl] = [];
        levels[lvl].push(n);
    });
    var levelKeys = Object.keys(levels).map(function (k) { return parseInt(k, 10); }).sort(function (a, b) { return a - b; });
    var totalLevels = levelKeys.length;
    console.log('Level distribution:', Object.fromEntries(levelKeys.map(function (level) { return [level, levels[level].length]; })));
    levelKeys.forEach(function (levelNum, levelIndex) {
        var group = levels[levelNum];
        var count = group.length || 1;
        // Enhanced spacing calculations for clearer hierarchy
        // Base spacing optimized for readability and clear visual hierarchy
        var baseVerticalSpacing = levelNum === 0 ? 250 : 200; // More space for root level
        var baseHorizontalSpacing = levelNum === 0 ? 350 : 280; // More space between root nodes
        // Scale spacing based on spreadFactor with more predictable results
        var verticalSpacing = baseVerticalSpacing * spreadFactor;
        var horizontalSpacing = baseHorizontalSpacing * spreadFactor;
        // Position calculation - center each level horizontally
        var y = center.y + (levelIndex - Math.floor(totalLevels / 2)) * verticalSpacing;
        var totalWidth = Math.max(0, (count - 1) * horizontalSpacing);
        var startX = center.x - totalWidth / 2;
        group.forEach(function (node, idx) {
            node.x = Math.round(startX + idx * horizontalSpacing);
            node.y = Math.round(y);
            console.log("Positioned node ".concat(node.id, " (level ").concat(levelNum, ") at (").concat(node.x, ", ").concat(node.y, ")"));
        });
    });
    console.log('Hierarchical layout completed');
    return nodes;
};
// Main Mind Map Tab Component with React Flow
var MindMapTab = function (_a) {
    var sessionId = _a.sessionId, documents = _a.documents, sessionData = _a.sessionData, generatedContent = _a.generatedContent, onNavigateToTab = _a.onNavigateToTab, onRefetchGeneratedContent = _a.onRefetchGeneratedContent;
    var theme = useTheme().theme;
    var _b = useToast(), showSuccess = _b.showSuccess, showError = _b.showError;
    var _c = useAuth(), currentUser = _c.currentUser, isAuthenticated = _c.isAuthenticated;
    // Progress tracking
    var progressTracking = useProgressTracking();
    // Debug authentication status
    useEffect(function () {
        console.log('MindMapTab Authentication Status:', {
            isAuthenticated: isAuthenticated,
            hasCurrentUser: !!currentUser,
            userEmail: currentUser === null || currentUser === void 0 ? void 0 : currentUser.email,
            sessionId: sessionId,
            hasProgressTracking: !!progressTracking
        });
    }, [isAuthenticated, currentUser, sessionId, progressTracking]);
    // React Flow states
    var _d = useNodesState([]), nodes = _d[0], setNodes = _d[1], onNodesChange = _d[2];
    var _e = useEdgesState([]), edges = _e[0], setEdges = _e[1], onEdgesChange = _e[2];
    // Mind map data states
    var _f = useState(null), mindMapData = _f[0], setMindMapData = _f[1];
    var _g = useState(false), isLoading = _g[0], setIsLoading = _g[1];
    var _h = useState(false), isGenerating = _h[0], setIsGenerating = _h[1];
    // UI states
    var _j = useState(null), selectedNode = _j[0], setSelectedNode = _j[1];
    var _k = useState(false), showHelp = _k[0], setShowHelp = _k[1];
    var _l = useState(''), searchQuery = _l[0], setSearchQuery = _l[1];
    var _m = useState(typeof window !== 'undefined' ? window.innerWidth : 1024), viewportWidth = _m[0], setViewportWidth = _m[1];
    var _o = useState(false), showMobileSidebar = _o[0], setShowMobileSidebar = _o[1];
    var _p = useState(null), nodeProgressData = _p[0], setNodeProgressData = _p[1];
    var _q = useState(function () {
        try {
            var s = sessionStorage.getItem('mindmap.controlsOpen');
            return s === null ? false : s === 'true';
        }
        catch (e) {
            return false;
        }
    }), controlsOpen = _q[0], setControlsOpen = _q[1];
    var _r = useState(false), showRegenerateConfirm = _r[0], setShowRegenerateConfirm = _r[1];
    // Node editing states
    var _s = useState(null), editingNode = _s[0], setEditingNode = _s[1];
    var _t = useState(''), editingNodeText = _t[0], setEditingNodeText = _t[1];
    var _u = useState(''), editingNodeDescription = _u[0], setEditingNodeDescription = _u[1];
    var _v = useState(false), showNodeEditDialog = _v[0], setShowNodeEditDialog = _v[1];
    // Simplified layout settings - fixed hierarchical layout
    var _w = useState(1.0), spreadFactor = _w[0], setSpreadFactor = _w[1]; // Changed from 300 to 1.0 to match UI range
    var _x = useState('Vibrant'), colorTheme = _x[0], setColorTheme = _x[1];
    var searchInputRef = useRef(null);
    var reactFlowWrapper = useRef(null);
    // Handle viewport changes for mobile responsiveness
    useEffect(function () {
        var handleResize = function () {
            setViewportWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return function () { return window.removeEventListener('resize', handleResize); };
    }, []);
    // Load persisted settings from localStorage on mount (only if no database data)
    useEffect(function () {
        // Only load from localStorage if we don't have sessionId (meaning no database data will be loaded)
        if (!sessionId) {
            try {
                var raw = localStorage.getItem('mindmap.settings');
                if (raw) {
                    var settings = JSON.parse(raw);
                    var localSpreadFactor = settings.spreadFactor || 1.0;
                    // Handle migration from old spreadFactor values
                    if (localSpreadFactor > 10) {
                        localSpreadFactor = Math.max(0.5, Math.min(3.0, localSpreadFactor / 300));
                        console.log("Converted legacy localStorage spread factor to ".concat(localSpreadFactor));
                    }
                    setSpreadFactor(localSpreadFactor);
                    setColorTheme(settings.colorTheme || 'Vibrant');
                }
            }
            catch (err) {
                console.warn('Failed to load mindmap settings:', err);
            }
        }
    }, [sessionId]); // Include sessionId in dependencies
    // Persist settings when they change
    useEffect(function () {
        try {
            var s = { spreadFactor: spreadFactor, colorTheme: colorTheme };
            localStorage.setItem('mindmap.settings', JSON.stringify(s));
        }
        catch (err) {
            console.warn('Failed to save mindmap settings:', err);
        }
    }, [spreadFactor, colorTheme]);
    // Color themes
    var colorThemes = [
        { name: 'Vibrant', colors: ['#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#06B6D4'] },
        { name: 'Pastel', colors: ['#DDD6FE', '#D1FAE5', '#FEF3C7', '#FEE2E2', '#DBEAFE'] },
        { name: 'Dark', colors: ['#374151', '#1F2937', '#111827', '#0F172A', '#18181B'] },
        { name: 'Earth', colors: ['#92400E', '#059669', '#1E40AF', '#7C2D12', '#B45309'] }
    ];
    // Get color based on node type and level
    var getNodeColor = useCallback(function (type, level) {
        var themes = colorThemes.find(function (theme) { return theme.name === colorTheme; });
        var colors = (themes === null || themes === void 0 ? void 0 : themes.colors) || colorThemes[0].colors;
        switch (type) {
            case 'main':
                return colors[0];
            case 'branch':
                return colors[(level % colors.length) + 1] || colors[1];
            case 'leaf':
                return colors[(level % colors.length) + 2] || colors[2];
            default:
                return colors[0];
        }
    }, [colorTheme]);
    // Node interaction handlers - declare first to avoid hoisting issues
    // Node interaction handlers - full editing capability enabled
    var handleNodeEdit = useCallback(function (nodeId) { return __awaiter(void 0, void 0, void 0, function () {
        var node, nodeData;
        return __generator(this, function (_a) {
            node = nodes.find(function (n) { return n.id === nodeId; });
            if (!node)
                return [2 /*return*/];
            nodeData = node.data;
            setEditingNode(nodeId);
            setEditingNodeText(nodeData.text || '');
            setEditingNodeDescription(nodeData.description || '');
            setShowNodeEditDialog(true);
            return [2 /*return*/];
        });
    }); }, [nodes]);
    var handleNodeSave = useCallback(function (nodeId, newText, newDescription) { return __awaiter(void 0, void 0, void 0, function () {
        var node, nodeData_1, updateMindMapNode, error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 4, , 5]);
                    node = nodes.find(function (n) { return n.id === nodeId; });
                    if (!node)
                        return [2 /*return*/];
                    nodeData_1 = node.data;
                    // Update local state immediately for responsive UI
                    setNodes(function (prev) { return prev.map(function (n) {
                        if (n.id === nodeId) {
                            var updatedData = __assign(__assign({}, n.data), { text: newText, description: newDescription || nodeData_1.description });
                            return __assign(__assign({}, n), { data: updatedData });
                        }
                        return n;
                    }); });
                    if (!(nodeData_1 === null || nodeData_1 === void 0 ? void 0 : nodeData_1.dbId)) return [3 /*break*/, 3];
                    return [4 /*yield*/, import('../../../services/apiClient')];
                case 1:
                    updateMindMapNode = (_a.sent()).updateMindMapNode;
                    return [4 /*yield*/, updateMindMapNode(nodeData_1.dbId, {
                            text: newText,
                            description: newDescription || nodeData_1.description
                        })];
                case 2:
                    _a.sent();
                    showSuccess('Node updated successfully');
                    _a.label = 3;
                case 3:
                    // Close editing dialog
                    setShowNodeEditDialog(false);
                    setEditingNode(null);
                    setEditingNodeText('');
                    setEditingNodeDescription('');
                    return [3 /*break*/, 5];
                case 4:
                    error_1 = _a.sent();
                    console.error('Failed to save node:', error_1);
                    showError('Failed to update node');
                    return [3 /*break*/, 5];
                case 5: return [2 /*return*/];
            }
        });
    }); }, [nodes, setNodes, showSuccess, showError]);
    var handleNodeDelete = useCallback(function (nodeId) { return __awaiter(void 0, void 0, void 0, function () {
        var node, nodeData, softDeleteMindMapNode, error_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    node = nodes.find(function (n) { return n.id === nodeId; });
                    nodeData = node === null || node === void 0 ? void 0 : node.data;
                    // Update local state
                    setNodes(function (prev) { return prev.filter(function (n) { return n.id !== nodeId; }); });
                    setEdges(function (prev) { return prev.filter(function (e) { return e.source !== nodeId && e.target !== nodeId; }); });
                    if (selectedNode === nodeId) {
                        setSelectedNode(null);
                    }
                    if (!(nodeData === null || nodeData === void 0 ? void 0 : nodeData.dbId)) return [3 /*break*/, 5];
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 4, , 5]);
                    return [4 /*yield*/, import('../../../services/apiClient')];
                case 2:
                    softDeleteMindMapNode = (_a.sent()).softDeleteMindMapNode;
                    return [4 /*yield*/, softDeleteMindMapNode(nodeData.dbId)];
                case 3:
                    _a.sent();
                    showSuccess('Node deleted successfully');
                    return [3 /*break*/, 5];
                case 4:
                    error_2 = _a.sent();
                    console.error('Failed to delete node from backend:', error_2);
                    showError('Failed to delete node');
                    return [3 /*break*/, 5];
                case 5: return [2 /*return*/];
            }
        });
    }); }, [setNodes, setEdges, selectedNode, nodes, showSuccess, showError]);
    var handleNodeColorChange = useCallback(function (nodeId, newColor) { return __awaiter(void 0, void 0, void 0, function () {
        var node, nodeData, updateMindMapNode, error_3;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    // Update local state
                    setNodes(function (prev) { return prev.map(function (node) {
                        if (node.id === nodeId) {
                            var newData = __assign({}, node.data);
                            newData.color = newColor;
                            return __assign(__assign({}, node), { data: newData });
                        }
                        return node;
                    }); });
                    // Update connected edges color
                    setEdges(function (prev) { return prev.map(function (edge) {
                        if (edge.source === nodeId) {
                            return __assign(__assign({}, edge), { data: __assign(__assign({}, edge.data), { color: newColor }), markerEnd: edge.markerEnd && typeof edge.markerEnd === 'object'
                                    ? __assign(__assign({}, edge.markerEnd), { color: newColor }) : { type: MarkerType.ArrowClosed, color: newColor } });
                        }
                        return edge;
                    }); });
                    node = nodes.find(function (n) { return n.id === nodeId; });
                    nodeData = node === null || node === void 0 ? void 0 : node.data;
                    if (!(nodeData === null || nodeData === void 0 ? void 0 : nodeData.dbId)) return [3 /*break*/, 5];
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 4, , 5]);
                    return [4 /*yield*/, import('../../../services/apiClient')];
                case 2:
                    updateMindMapNode = (_a.sent()).updateMindMapNode;
                    return [4 /*yield*/, updateMindMapNode(nodeData.dbId, {
                            color: newColor
                        })];
                case 3:
                    _a.sent();
                    return [3 /*break*/, 5];
                case 4:
                    error_3 = _a.sent();
                    console.error('Failed to update node color in backend:', error_3);
                    return [3 /*break*/, 5];
                case 5: return [2 /*return*/];
            }
        });
    }); }, [setNodes, setEdges, nodes]);
    var handleNodePositionSave = useCallback(function (nodeId, newPosition) { return __awaiter(void 0, void 0, void 0, function () {
        var node, nodeData, updateNodePosition, error_4;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    node = nodes.find(function (n) { return n.id === nodeId; });
                    nodeData = node === null || node === void 0 ? void 0 : node.data;
                    console.log('handleNodePositionSave called:', {
                        nodeId: nodeId,
                        newPosition: newPosition,
                        nodeFound: !!node,
                        hasDbId: !!(nodeData === null || nodeData === void 0 ? void 0 : nodeData.dbId),
                        dbId: nodeData === null || nodeData === void 0 ? void 0 : nodeData.dbId
                    });
                    if (!(nodeData === null || nodeData === void 0 ? void 0 : nodeData.dbId)) return [3 /*break*/, 5];
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 4, , 5]);
                    return [4 /*yield*/, import('../../../services/apiClient')];
                case 2:
                    updateNodePosition = (_a.sent()).updateNodePosition;
                    return [4 /*yield*/, updateNodePosition(nodeData.dbId, newPosition.x, newPosition.y)];
                case 3:
                    _a.sent();
                    console.log('Position saved successfully to database');
                    return [3 /*break*/, 5];
                case 4:
                    error_4 = _a.sent();
                    console.error('Failed to update node position in backend:', error_4);
                    return [3 /*break*/, 5];
                case 5: return [2 /*return*/];
            }
        });
    }); }, [nodes]);
    var handleNodeToggleExpansion = useCallback(function (nodeId) {
        setNodes(function (prev) { return prev.map(function (node) {
            if (node.id === nodeId) {
                var newData = __assign({}, node.data);
                newData.isExpanded = !newData.isExpanded;
                return __assign(__assign({}, node), { data: newData });
            }
            return node;
        }); });
        // Regenerate edges based on expansion state
        // This would need to be implemented based on your connection logic
    }, [setNodes]);
    var handleNodeToggleBookmark = useCallback(function (nodeId) { return __awaiter(void 0, void 0, void 0, function () {
        var node, nodeData, updateMindMapNode, error_5;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setNodes(function (prev) { return prev.map(function (node) {
                        if (node.id === nodeId) {
                            var newData = __assign({}, node.data);
                            newData.isBookmarked = !newData.isBookmarked;
                            return __assign(__assign({}, node), { data: newData });
                        }
                        return node;
                    }); });
                    node = nodes.find(function (n) { return n.id === nodeId; });
                    nodeData = node === null || node === void 0 ? void 0 : node.data;
                    if (!(nodeData === null || nodeData === void 0 ? void 0 : nodeData.dbId)) return [3 /*break*/, 5];
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 4, , 5]);
                    return [4 /*yield*/, import('../../../services/apiClient')];
                case 2:
                    updateMindMapNode = (_a.sent()).updateMindMapNode;
                    return [4 /*yield*/, updateMindMapNode(nodeData.dbId, {
                            is_bookmarked: nodeData.isBookmarked
                        })];
                case 3:
                    _a.sent();
                    return [3 /*break*/, 5];
                case 4:
                    error_5 = _a.sent();
                    console.error('Failed to update bookmark in backend:', error_5);
                    return [3 /*break*/, 5];
                case 5: return [2 /*return*/];
            }
        });
    }); }, [setNodes, nodes]);
    // Progress tracking handlers
    var handleGenerateQuiz = useCallback(function (nodeId, options) { return __awaiter(void 0, void 0, void 0, function () {
        var nodeData, readyDocuments, documentIds, result, error_6;
        var _a, _b, _c, _d, _e;
        return __generator(this, function (_f) {
            switch (_f.label) {
                case 0:
                    _f.trys.push([0, 6, , 7]);
                    // Check authentication first
                    if (!isAuthenticated || !currentUser) {
                        showError('You must be logged in to generate quizzes.');
                        return [2 /*return*/];
                    }
                    nodeData = (_a = nodes.find(function (n) { return n.id === nodeId; })) === null || _a === void 0 ? void 0 : _a.data;
                    if (!nodeData) {
                        showError('Node data not found.');
                        return [2 /*return*/];
                    }
                    showSuccess("Generating ".concat(options.difficulty || 'medium', " quiz with ").concat(options.question_count || 5, " questions for \"").concat(nodeData.text, "\""));
                    readyDocuments = documents.filter(function (doc) { return doc.status === 'ready'; });
                    documentIds = readyDocuments.map(function (doc) { return doc.id; });
                    if (documentIds.length === 0) {
                        showError('No documents available for quiz generation.');
                        return [2 /*return*/];
                    }
                    // Ensure we have a valid session ID
                    if (!sessionId) {
                        showError('No session available for quiz generation.');
                        return [2 /*return*/];
                    }
                    console.log('Generating quiz with:', {
                        sessionId: sessionId,
                        documentIds: documentIds,
                        nodeId: nodeData.dbId,
                        nodeText: nodeData.text,
                        options: options,
                        isAuthenticated: isAuthenticated,
                        userEmail: currentUser === null || currentUser === void 0 ? void 0 : currentUser.email
                    });
                    return [4 /*yield*/, generateQuiz(sessionId, documentIds, {
                            difficulty: options.difficulty || 'medium',
                            numQuestions: options.question_count || 5,
                            nodeText: options.nodeText || nodeData.text,
                            customPrompt: options.customPrompt,
                            focusAreas: options.focusAreas,
                            nodeId: nodeData.dbId // Pass database node ID for tracking
                        })];
                case 1:
                    result = _f.sent();
                    if (!(result.questions && result.questions.length > 0)) return [3 /*break*/, 4];
                    // Store the generated context for the quiz tab
                    sessionStorage.setItem('generatedQuizData', JSON.stringify({
                        nodeId: nodeId,
                        nodeText: nodeData.text,
                        quizData: result,
                        options: options
                    }));
                    showSuccess("Generated ".concat(result.questions.length, " quiz questions successfully!"));
                    if (!onRefetchGeneratedContent) return [3 /*break*/, 3];
                    return [4 /*yield*/, onRefetchGeneratedContent()];
                case 2:
                    _f.sent();
                    _f.label = 3;
                case 3:
                    // Navigate to quiz tab
                    if (onNavigateToTab) {
                        onNavigateToTab('quiz');
                    }
                    return [3 /*break*/, 5];
                case 4:
                    showError('Failed to generate quiz questions. Please try again.');
                    _f.label = 5;
                case 5: return [3 /*break*/, 7];
                case 6:
                    error_6 = _f.sent();
                    console.error('Failed to generate quiz:', error_6);
                    // Check for specific error types
                    if (((_b = error_6.response) === null || _b === void 0 ? void 0 : _b.status) === 401) {
                        showError('Authentication failed. Please log in again.');
                    }
                    else if (((_c = error_6.response) === null || _c === void 0 ? void 0 : _c.status) === 403) {
                        showError('Access denied. You may need to upgrade your account.');
                    }
                    else if (((_d = error_6.response) === null || _d === void 0 ? void 0 : _d.status) === 429) {
                        showError('Too many requests. Please wait a moment and try again.');
                    }
                    else if (((_e = error_6.response) === null || _e === void 0 ? void 0 : _e.status) === 500) {
                        showError('Server error. Please try again later.');
                    }
                    else {
                        showError("Failed to generate quiz: ".concat(error_6.message || 'Unknown error'));
                    }
                    return [3 /*break*/, 7];
                case 7: return [2 /*return*/];
            }
        });
    }); }, [nodes, documents, sessionId, showSuccess, showError, onNavigateToTab, onRefetchGeneratedContent, isAuthenticated, currentUser]);
    var handleGenerateFlashcards = useCallback(function (nodeId, options) { return __awaiter(void 0, void 0, void 0, function () {
        var nodeData, readyDocuments, documentIds, result, error_7;
        var _a, _b, _c, _d, _e;
        return __generator(this, function (_f) {
            switch (_f.label) {
                case 0:
                    _f.trys.push([0, 6, , 7]);
                    // Check authentication first
                    if (!isAuthenticated || !currentUser) {
                        showError('You must be logged in to generate flashcards.');
                        return [2 /*return*/];
                    }
                    nodeData = (_a = nodes.find(function (n) { return n.id === nodeId; })) === null || _a === void 0 ? void 0 : _a.data;
                    if (!nodeData) {
                        showError('Node data not found.');
                        return [2 /*return*/];
                    }
                    showSuccess("Generating ".concat(options.card_count || 10, " ").concat(options.difficulty || 'medium', " flashcards for \"").concat(nodeData.text, "\""));
                    readyDocuments = documents.filter(function (doc) { return doc.status === 'ready'; });
                    documentIds = readyDocuments.map(function (doc) { return doc.id; });
                    if (documentIds.length === 0) {
                        showError('No documents available for flashcard generation.');
                        return [2 /*return*/];
                    }
                    // Ensure we have a valid session ID
                    if (!sessionId) {
                        showError('No session available for flashcard generation.');
                        return [2 /*return*/];
                    }
                    console.log('Generating flashcards with:', {
                        sessionId: sessionId,
                        documentIds: documentIds,
                        nodeId: nodeData.dbId,
                        nodeText: nodeData.text,
                        options: options,
                        isAuthenticated: isAuthenticated,
                        userEmail: currentUser === null || currentUser === void 0 ? void 0 : currentUser.email
                    });
                    return [4 /*yield*/, generateFlashcards(sessionId, documentIds, {
                            numFlashcards: options.card_count || 10,
                            difficulty: options.difficulty || 'medium',
                            nodeText: options.nodeText || nodeData.text,
                            customPrompt: options.customPrompt,
                            focusAreas: options.focusAreas,
                            nodeId: nodeData.dbId // Pass database node ID for tracking
                        })];
                case 1:
                    result = _f.sent();
                    if (!(result.flashcards && result.flashcards.length > 0)) return [3 /*break*/, 4];
                    // Store the generated context for the flashcards tab
                    sessionStorage.setItem('generatedFlashcardsData', JSON.stringify({
                        nodeId: nodeId,
                        nodeText: nodeData.text,
                        flashcardsData: result,
                        options: options
                    }));
                    showSuccess("Generated ".concat(result.flashcards.length, " flashcards successfully!"));
                    if (!onRefetchGeneratedContent) return [3 /*break*/, 3];
                    return [4 /*yield*/, onRefetchGeneratedContent()];
                case 2:
                    _f.sent();
                    _f.label = 3;
                case 3:
                    // Navigate to flashcards tab
                    if (onNavigateToTab) {
                        onNavigateToTab('flashcards');
                    }
                    return [3 /*break*/, 5];
                case 4:
                    showError('Failed to generate flashcards. Please try again.');
                    _f.label = 5;
                case 5: return [3 /*break*/, 7];
                case 6:
                    error_7 = _f.sent();
                    console.error('Failed to generate flashcards:', error_7);
                    // Check for specific error types
                    if (((_b = error_7.response) === null || _b === void 0 ? void 0 : _b.status) === 401) {
                        showError('Authentication failed. Please log in again.');
                    }
                    else if (((_c = error_7.response) === null || _c === void 0 ? void 0 : _c.status) === 403) {
                        showError('Access denied. You may need to upgrade your account.');
                    }
                    else if (((_d = error_7.response) === null || _d === void 0 ? void 0 : _d.status) === 429) {
                        showError('Too many requests. Please wait a moment and try again.');
                    }
                    else if (((_e = error_7.response) === null || _e === void 0 ? void 0 : _e.status) === 500) {
                        showError('Server error. Please try again later.');
                    }
                    else {
                        showError("Failed to generate flashcards: ".concat(error_7.message || 'Unknown error'));
                    }
                    return [3 /*break*/, 7];
                case 7: return [2 /*return*/];
            }
        });
    }); }, [nodes, documents, sessionId, showSuccess, showError, onNavigateToTab, onRefetchGeneratedContent, isAuthenticated, currentUser]);
    var handleUpdateProgress = useCallback(function (nodeId, updates) { return __awaiter(void 0, void 0, void 0, function () {
        var node, nodeData, dbId, updatedProgress, error_8;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 3, , 4]);
                    node = nodes.find(function (n) { return n.id === nodeId; });
                    nodeData = node === null || node === void 0 ? void 0 : node.data;
                    dbId = nodeData === null || nodeData === void 0 ? void 0 : nodeData.dbId;
                    if (!dbId) {
                        console.error('Node database ID not found for node:', nodeId);
                        showError('Cannot update progress: Node database ID not found');
                        return [2 /*return*/];
                    }
                    // Update the progress tracking backend using database UUID
                    return [4 /*yield*/, progressTracking.updateNodeProgress(dbId, updates)];
                case 1:
                    // Update the progress tracking backend using database UUID
                    _a.sent();
                    // Update local node data if needed
                    if (updates.is_bookmarked !== undefined) {
                        setNodes(function (prev) { return prev.map(function (node) {
                            if (node.id === nodeId) {
                                var newData = __assign({}, node.data);
                                newData.isBookmarked = updates.is_bookmarked;
                                return __assign(__assign({}, node), { data: newData });
                            }
                            return node;
                        }); });
                    }
                    return [4 /*yield*/, progressTracking.getNodeProgress(dbId)];
                case 2:
                    updatedProgress = _a.sent();
                    setNodeProgressData(updatedProgress);
                    showSuccess('Progress updated successfully');
                    return [3 /*break*/, 4];
                case 3:
                    error_8 = _a.sent();
                    console.error('Failed to update progress:', error_8);
                    showError('Failed to update progress. Please try again.');
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); }, [progressTracking, selectedNode, setNodes, showSuccess, showError]);
    var handleCreateGoal = useCallback(function (nodeId, goal) { return __awaiter(void 0, void 0, void 0, function () {
        var error_9;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, progressTracking.createGoal(goal)];
                case 1:
                    _a.sent();
                    showSuccess("Goal \"".concat(goal.goal_name, "\" created successfully"));
                    return [3 /*break*/, 3];
                case 2:
                    error_9 = _a.sent();
                    console.error('Failed to create goal:', error_9);
                    showError('Failed to create goal. Please try again.');
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); }, [progressTracking, showSuccess, showError]);
    var handleNodeAddChild = useCallback(function (parentNodeId) { return __awaiter(void 0, void 0, void 0, function () {
        var parentNode, parentData, newId, findOptimalPosition, newNodeData, newNode, newEdge, _a, createMindMapNode, createMindMapConnection, createdNode_1, error_10;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    parentNode = nodes.find(function (n) { return n.id === parentNodeId; });
                    if (!parentNode)
                        return [2 /*return*/];
                    parentData = parentNode.data;
                    newId = "node_".concat(Date.now());
                    findOptimalPosition = function (parentPos) {
                        var baseDistance = 120; // Much closer to parent - standard React Flow practice
                        var minDistance = 80; // Minimum distance to avoid overlap
                        // Try positions in a systematic pattern: right, bottom, left, top, then diagonals
                        var positions = [
                            { x: parentPos.x + baseDistance, y: parentPos.y },
                            { x: parentPos.x, y: parentPos.y + baseDistance },
                            { x: parentPos.x - baseDistance, y: parentPos.y },
                            { x: parentPos.x, y: parentPos.y - baseDistance },
                            { x: parentPos.x + baseDistance * 0.7, y: parentPos.y + baseDistance * 0.7 },
                            { x: parentPos.x - baseDistance * 0.7, y: parentPos.y + baseDistance * 0.7 },
                            { x: parentPos.x + baseDistance * 0.7, y: parentPos.y - baseDistance * 0.7 },
                            { x: parentPos.x - baseDistance * 0.7, y: parentPos.y - baseDistance * 0.7 }, // Top-left
                        ];
                        var _loop_1 = function (pos) {
                            var hasConflict = nodes.some(function (node) {
                                var distance = Math.sqrt(Math.pow(node.position.x - pos.x, 2) +
                                    Math.pow(node.position.y - pos.y, 2));
                                return distance < minDistance;
                            });
                            if (!hasConflict) {
                                return { value: pos };
                            }
                        };
                        // Find the first position without conflicts
                        for (var _i = 0, positions_1 = positions; _i < positions_1.length; _i++) {
                            var pos = positions_1[_i];
                            var state_1 = _loop_1(pos);
                            if (typeof state_1 === "object")
                                return state_1.value;
                        }
                        // Fallback: place slightly to the right and below parent
                        return {
                            x: parentPos.x + baseDistance,
                            y: parentPos.y + baseDistance,
                        };
                    };
                    newNodeData = {
                        text: 'New Concept',
                        type: parentData.level < 2 ? 'branch' : 'leaf',
                        color: getNodeColor(parentData.level < 2 ? 'branch' : 'leaf', parentData.level + 1),
                        level: parentData.level + 1,
                        isExpanded: true,
                        progress: 0,
                        document_id: parentData.document_id,
                        hasChildren: false,
                        isMobile: viewportWidth < 640,
                        onEdit: handleNodeEdit,
                        onSave: handleNodeSave,
                        onDelete: handleNodeDelete,
                        onToggleExpansion: handleNodeToggleExpansion,
                        onToggleBookmark: handleNodeToggleBookmark,
                        onAddChild: handleNodeAddChild,
                        onColorChange: handleNodeColorChange,
                        isEditing: false,
                        editText: '',
                        onEditTextChange: function (text) { return setEditingNodeText(text); },
                    };
                    newNode = {
                        id: newId,
                        type: 'mindMapNode',
                        position: findOptimalPosition(parentNode.position),
                        data: newNodeData,
                        draggable: true,
                        selectable: true,
                        connectable: true,
                        deletable: true, // Essential for React Flow - allows deletion
                    };
                    newEdge = {
                        id: "".concat(parentNodeId, "-").concat(newId),
                        source: parentNodeId,
                        target: newId,
                        type: 'mindMapEdge',
                        data: {
                            connectionType: 'default',
                            isBidirectional: false,
                            color: parentData.color,
                        },
                        markerEnd: {
                            type: MarkerType.ArrowClosed,
                            color: parentData.color || '#8B5CF6',
                        },
                    };
                    console.log('Creating new child node and edge:', {
                        newNode: { id: newId, position: newNode.position, text: newNodeData.text },
                        newEdge: { id: newEdge.id, source: newEdge.source, target: newEdge.target },
                        parentData: { id: parentNodeId, dbId: parentData.dbId },
                        parentPosition: parentNode.position,
                        calculatedPosition: newNode.position,
                        isMoveable: newNode.draggable
                    });
                    setNodes(function (prev) { return __spreadArray(__spreadArray([], prev, true), [newNode], false); });
                    setEdges(function (prev) { return __spreadArray(__spreadArray([], prev, true), [newEdge], false); });
                    // Update parent to show it has children and add connection to the new child
                    setNodes(function (prev) { return prev.map(function (node) {
                        if (node.id === parentNodeId) {
                            var newData = __assign({}, node.data);
                            newData.hasChildren = true;
                            // Add the new child to parent's connections array
                            var currentConnections = Array.isArray(newData.connections) ? newData.connections : [];
                            if (!currentConnections.includes(newId)) {
                                newData.connections = __spreadArray(__spreadArray([], currentConnections, true), [newId], false);
                            }
                            return __assign(__assign({}, node), { data: newData });
                        }
                        return node;
                    }); });
                    // Select the new node
                    setSelectedNode(newId);
                    // Automatically open edit dialog for new nodes
                    setEditingNode(newId);
                    setEditingNodeText('New Concept');
                    setEditingNodeDescription('');
                    setShowNodeEditDialog(true);
                    // Show success feedback
                    showSuccess('New concept added successfully! Please add a name and description.');
                    if (!((mindMapData === null || mindMapData === void 0 ? void 0 : mindMapData.id) && sessionId)) return [3 /*break*/, 8];
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 7, , 8]);
                    return [4 /*yield*/, import('../../../services/apiClient')];
                case 2:
                    _a = _b.sent(), createMindMapNode = _a.createMindMapNode, createMindMapConnection = _a.createMindMapConnection;
                    return [4 /*yield*/, createMindMapNode(mindMapData.id, {
                            external_id: newId,
                            text: newNodeData.text,
                            type: newNodeData.type,
                            level: newNodeData.level,
                            x_position: newNode.position.x,
                            y_position: newNode.position.y,
                            color: newNodeData.color,
                            is_expanded: newNodeData.isExpanded,
                            description: newNodeData.description || ''
                        })];
                case 3:
                    createdNode_1 = _b.sent();
                    if (!createdNode_1.node) return [3 /*break*/, 6];
                    setNodes(function (prev) { return prev.map(function (node) {
                        if (node.id === newId) {
                            var updatedData = __assign({}, node.data);
                            updatedData.dbId = createdNode_1.node.id;
                            return __assign(__assign({}, node), { data: updatedData });
                        }
                        return node;
                    }); });
                    if (!parentData.dbId) return [3 /*break*/, 5];
                    console.log('Creating connection in database:', {
                        parentDbId: parentData.dbId,
                        childDbId: createdNode_1.node.id,
                        mindMapId: mindMapData.id
                    });
                    return [4 /*yield*/, createMindMapConnection(mindMapData.id, {
                            source_node_id: parentData.dbId,
                            target_node_id: createdNode_1.node.id,
                            connection_type: 'default'
                        })];
                case 4:
                    _b.sent();
                    console.log('Connection created successfully in database');
                    return [3 /*break*/, 6];
                case 5:
                    console.warn('Parent node does not have dbId, cannot create database connection');
                    _b.label = 6;
                case 6: return [3 /*break*/, 8];
                case 7:
                    error_10 = _b.sent();
                    console.error('Failed to create node in backend:', error_10);
                    showError('Failed to save new node');
                    return [3 /*break*/, 8];
                case 8: return [2 /*return*/];
            }
        });
    }); }, [nodes, setNodes, setEdges, getNodeColor, viewportWidth, mindMapData, sessionId, handleNodeEdit, handleNodeSave, handleNodeDelete, handleNodeToggleExpansion, handleNodeToggleBookmark, handleNodeColorChange]);
    // Add a new root node to the mind map
    var handleAddRootNode = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var newId, findRootPosition, newNodeData, newNode, createMindMapNode, createdNode_2, error_11;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    newId = "node_".concat(Date.now());
                    findRootPosition = function () {
                        var margin = 200; // Reduced margin for closer placement
                        var baseDistance = 250; // Much closer than previous 400
                        // If no nodes exist, place at center
                        if (nodes.length === 0) {
                            return { x: 400, y: 300 };
                        }
                        // Try positions in a systematic spiral pattern
                        for (var ring = 1; ring <= 4; ring++) {
                            var nodesInRing = 6 * ring; // 6, 12, 18, 24 nodes per ring
                            var distance = baseDistance * ring;
                            var _loop_2 = function (i) {
                                var angle = (i / nodesInRing) * 2 * Math.PI;
                                var newX = Math.cos(angle) * distance;
                                var newY = Math.sin(angle) * distance;
                                // Check if this position conflicts with existing nodes
                                var hasConflict = nodes.some(function (node) {
                                    var dist = Math.sqrt(Math.pow(node.position.x - newX, 2) +
                                        Math.pow(node.position.y - newY, 2));
                                    return dist < margin;
                                });
                                if (!hasConflict) {
                                    return { value: { x: newX, y: newY } };
                                }
                            };
                            for (var i = 0; i < nodesInRing; i++) {
                                var state_2 = _loop_2(i);
                                if (typeof state_2 === "object")
                                    return state_2.value;
                            }
                        }
                        // Fallback: place at a reasonable distance from the center
                        var existingNodes = nodes.length;
                        return {
                            x: Math.cos(existingNodes * 0.5) * (baseDistance + existingNodes * 50),
                            y: Math.sin(existingNodes * 0.5) * (baseDistance + existingNodes * 50),
                        };
                    };
                    newNodeData = {
                        text: 'New Topic',
                        type: 'main',
                        color: getNodeColor('main', 0),
                        level: 0,
                        isExpanded: true,
                        progress: 0,
                        document_id: (_a = documents === null || documents === void 0 ? void 0 : documents[0]) === null || _a === void 0 ? void 0 : _a.id,
                        hasChildren: false,
                        isMobile: viewportWidth < 640,
                        onEdit: handleNodeEdit,
                        onSave: handleNodeSave,
                        onDelete: handleNodeDelete,
                        onToggleExpansion: handleNodeToggleExpansion,
                        onToggleBookmark: handleNodeToggleBookmark,
                        onAddChild: handleNodeAddChild,
                        onColorChange: handleNodeColorChange,
                        isEditing: false,
                        editText: '',
                        onEditTextChange: function (text) { return setEditingNodeText(text); },
                    };
                    newNode = {
                        id: newId,
                        type: 'mindMapNode',
                        position: findRootPosition(),
                        data: newNodeData,
                        draggable: true,
                        selectable: true,
                        connectable: true,
                        deletable: true, // Essential for React Flow - allows deletion
                    };
                    setNodes(function (prev) { return __spreadArray(__spreadArray([], prev, true), [newNode], false); });
                    // Select the new node
                    setSelectedNode(newId);
                    // Automatically open edit dialog for new root nodes
                    setEditingNode(newId);
                    setEditingNodeText('New Topic');
                    setEditingNodeDescription('');
                    setShowNodeEditDialog(true);
                    // Show success feedback
                    showSuccess('New topic added successfully! Please add a name and description.');
                    if (!((mindMapData === null || mindMapData === void 0 ? void 0 : mindMapData.id) && sessionId)) return [3 /*break*/, 5];
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 4, , 5]);
                    return [4 /*yield*/, import('../../../services/apiClient')];
                case 2:
                    createMindMapNode = (_b.sent()).createMindMapNode;
                    return [4 /*yield*/, createMindMapNode(mindMapData.id, {
                            external_id: newId,
                            text: newNodeData.text,
                            type: newNodeData.type,
                            level: newNodeData.level,
                            x_position: newNode.position.x,
                            y_position: newNode.position.y,
                            color: newNodeData.color,
                            is_expanded: newNodeData.isExpanded,
                            description: newNodeData.description || ''
                        })];
                case 3:
                    createdNode_2 = _b.sent();
                    // Update the node with the database ID from the response
                    if (createdNode_2.node) {
                        setNodes(function (prev) { return prev.map(function (node) {
                            if (node.id === newId) {
                                var updatedData = __assign({}, node.data);
                                updatedData.dbId = createdNode_2.node.id;
                                return __assign(__assign({}, node), { data: updatedData });
                            }
                            return node;
                        }); });
                    }
                    return [3 /*break*/, 5];
                case 4:
                    error_11 = _b.sent();
                    console.error('Failed to create root node in backend:', error_11);
                    showError('Failed to save new topic');
                    return [3 /*break*/, 5];
                case 5: return [2 /*return*/];
            }
        });
    }); }, [nodes, setNodes, getNodeColor, viewportWidth, documents, mindMapData, sessionId, handleNodeEdit, handleNodeSave, handleNodeDelete, handleNodeToggleExpansion, handleNodeToggleBookmark, handleNodeAddChild, handleNodeColorChange]);
    // Keyboard shortcuts for better UX - simplified without editing
    useEffect(function () {
        var handleKeyPress = function (event) {
            var _a, _b;
            // Only handle shortcuts when not in input fields
            if (((_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.tagName) === 'INPUT' || ((_b = document.activeElement) === null || _b === void 0 ? void 0 : _b.tagName) === 'TEXTAREA') {
                return;
            }
            // Ctrl/Cmd + N: Add new root node
            if ((event.ctrlKey || event.metaKey) && event.key === 'n') {
                event.preventDefault();
                handleAddRootNode();
            }
            // Delete key: Delete selected node
            if (event.key === 'Delete' && selectedNode) {
                event.preventDefault();
                handleNodeDelete(selectedNode);
            }
            // Tab: Add child to selected node
            if (event.key === 'Tab' && selectedNode) {
                event.preventDefault();
                handleNodeAddChild(selectedNode);
            }
        };
        document.addEventListener('keydown', handleKeyPress);
        return function () { return document.removeEventListener('keydown', handleKeyPress); };
    }, [selectedNode, handleAddRootNode, handleNodeDelete, handleNodeAddChild]);
    // Enhanced nodes change handler with position persistence
    var handleNodesChange = useCallback(function (changes) {
        onNodesChange(changes);
        // Save position changes to backend
        changes.forEach(function (change) {
            if (change.type === 'position' && change.dragging === false) {
                // Position change completed (drag ended)
                handleNodePositionSave(change.id, change.position);
            }
        });
    }, [onNodesChange, handleNodePositionSave]);
    // Convert mind map nodes to React Flow nodes
    var convertToReactFlowNodes = useCallback(function (mindMapNodes) {
        return mindMapNodes.map(function (node) { return ({
            id: node.id,
            type: 'mindMapNode',
            position: { x: node.x || 0, y: node.y || 0 },
            data: {
                text: node.text,
                type: node.type,
                color: node.color || getNodeColor(node.type, node.level),
                level: node.level,
                isExpanded: node.isExpanded,
                description: node.description,
                document_id: node.document_id,
                resources: node.resources,
                progress: node.progress,
                lastVisited: node.lastVisited,
                isBookmarked: node.isBookmarked,
                dbId: node.dbId,
                hasChildren: node.connections.length > 0,
                isMobile: viewportWidth < 640,
                onEdit: handleNodeEdit,
                onSave: handleNodeSave,
                onDelete: handleNodeDelete,
                onToggleExpansion: handleNodeToggleExpansion,
                onToggleBookmark: handleNodeToggleBookmark,
                onAddChild: handleNodeAddChild,
                onColorChange: handleNodeColorChange,
                isEditing: editingNode === node.id,
                editText: editingNode === node.id ? editingNodeText : '',
                onEditTextChange: function (text) { return setEditingNodeText(text); },
            },
            selected: selectedNode === node.id,
            draggable: true,
            selectable: true,
            connectable: true,
            deletable: true, // Essential for React Flow - allows deletion
        }); });
    }, [getNodeColor, viewportWidth, selectedNode, handleNodeEdit, handleNodeSave, handleNodeDelete, handleNodeToggleExpansion, handleNodeToggleBookmark, handleNodeAddChild, handleNodeColorChange, editingNode, editingNodeText]);
    // Convert connections to React Flow edges with duplicate prevention
    var convertToReactFlowEdges = useCallback(function (mindMapNodes, connections) {
        var edges = [];
        var edgeSet = new Set(); // Track created edges to prevent duplicates
        console.log('Converting to React Flow edges:', {
            mindMapNodes: mindMapNodes.length,
            connections: (connections === null || connections === void 0 ? void 0 : connections.length) || 0,
            nodeConnections: mindMapNodes.map(function (n) { return ({ id: n.id, connections: n.connections }); })
        });
        // Helper function to create a unique edge identifier
        var createEdgeKey = function (sourceId, targetId) {
            // Sort to handle bidirectional edges consistently
            return sourceId < targetId ? "".concat(sourceId, "->").concat(targetId) : "".concat(targetId, "->").concat(sourceId);
        };
        // Create edges from node connections (primary method)
        mindMapNodes.forEach(function (node) {
            node.connections.forEach(function (targetId) {
                var targetNode = mindMapNodes.find(function (n) { return n.id === targetId; });
                if (targetNode && (!targetNode.isExpanded === false || node.isExpanded !== false)) {
                    var edgeKey = createEdgeKey(node.id, targetId);
                    var edgeId = "".concat(node.id, "-").concat(targetId);
                    // Check for duplicates
                    if (!edgeSet.has(edgeKey)) {
                        edgeSet.add(edgeKey);
                        console.log('Creating edge from node connections:', edgeId);
                        edges.push({
                            id: edgeId,
                            source: node.id,
                            target: targetId,
                            type: 'mindMapEdge',
                            data: {
                                connectionType: 'default',
                                isBidirectional: false,
                                color: node.color || getNodeColor(node.type, node.level),
                                isHighlighted: selectedNode === node.id || selectedNode === targetId,
                                isSearchHighlight: searchQuery.trim() && (node.text.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                    targetNode.text.toLowerCase().includes(searchQuery.toLowerCase())),
                            },
                            markerEnd: {
                                type: MarkerType.ArrowClosed,
                                color: node.color || getNodeColor(node.type, node.level),
                            },
                        });
                    }
                }
            });
        });
        // Fallback: Create edges from database connections if node connections are empty
        if (edges.length === 0 && connections && connections.length > 0) {
            console.log('No edges from node connections, using database connections as fallback');
            edgeSet.clear(); // Reset edge tracking for fallback
            connections.forEach(function (conn) {
                var sourceNode = mindMapNodes.find(function (n) { return n.dbId === conn.source_node_id || n.id === conn.source_node_id; });
                var targetNode = mindMapNodes.find(function (n) { return n.dbId === conn.target_node_id || n.id === conn.target_node_id; });
                if (sourceNode && targetNode) {
                    var edgeKey = createEdgeKey(sourceNode.id, targetNode.id);
                    var edgeId = "".concat(sourceNode.id, "-").concat(targetNode.id);
                    // Check for duplicates
                    if (!edgeSet.has(edgeKey)) {
                        edgeSet.add(edgeKey);
                        console.log('Creating edge from database connections:', edgeId);
                        edges.push({
                            id: edgeId,
                            source: sourceNode.id,
                            target: targetNode.id,
                            type: 'mindMapEdge',
                            data: {
                                connectionType: conn.connection_type || 'default',
                                isBidirectional: conn.is_bidirectional || false,
                                color: sourceNode.color || getNodeColor(sourceNode.type, sourceNode.level),
                                isHighlighted: selectedNode === sourceNode.id || selectedNode === targetNode.id,
                                isSearchHighlight: searchQuery.trim() && (sourceNode.text.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                    targetNode.text.toLowerCase().includes(searchQuery.toLowerCase())),
                            },
                            markerEnd: {
                                type: MarkerType.ArrowClosed,
                                color: sourceNode.color || getNodeColor(sourceNode.type, sourceNode.level),
                            },
                        });
                    }
                }
                else {
                    console.warn('Could not find nodes for connection:', conn);
                }
            });
        }
        console.log('Generated edges (after deduplication):', edges.length, edges);
        return edges;
    }, [getNodeColor, selectedNode, searchQuery]);
    // Apply hierarchical layout algorithm - enhanced for better organization and consistency
    var applyLayout = useCallback(function (mindMapNodes, forceLayout) {
        if (forceLayout === void 0) { forceLayout = false; }
        // Always apply the layout algorithm for consistency, regardless of database positions
        // This ensures that the mind map looks the same whether generated fresh or loaded from database
        console.log('Applying hierarchical layout algorithm for consistency');
        var center = { x: 500, y: 400 }; // Consistent center position
        var layoutedNodes = applyHierarchicalLayout(__spreadArray([], mindMapNodes, true), center, spreadFactor);
        // Ensure proper spacing and no overlapping for all layouts
        return layoutedNodes.map(function (node) { return (__assign(__assign({}, node), { x: Math.round(node.x || 0), y: Math.round(node.y || 0) })); });
    }, [spreadFactor]);
    // Load existing mind map data from backend
    useEffect(function () {
        var loadMindMaps = function () { return __awaiter(void 0, void 0, void 0, function () {
            var listSessionMindMaps, mindMapsResponse, latestMindMap, getMindMap, mindMapData_1, dbSpreadFactor, dbColorTheme, processedNodes, hasCustomPositions, finalNodes, reactFlowNodes, reactFlowEdges, error_12;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!sessionId) {
                            if (generatedContent === null || generatedContent === void 0 ? void 0 : generatedContent.mindmap) {
                                loadFromGeneratedContent();
                            }
                            return [2 /*return*/];
                        }
                        setIsLoading(true);
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 8, 9, 10]);
                        console.log('Loading mind maps for session:', sessionId);
                        return [4 /*yield*/, import('../../../services/apiClient')];
                    case 2:
                        listSessionMindMaps = (_a.sent()).listSessionMindMaps;
                        return [4 /*yield*/, listSessionMindMaps(sessionId)];
                    case 3:
                        mindMapsResponse = _a.sent();
                        if (!(mindMapsResponse.mind_maps && mindMapsResponse.mind_maps.length > 0)) return [3 /*break*/, 6];
                        latestMindMap = mindMapsResponse.mind_maps[0];
                        return [4 /*yield*/, import('../../../services/apiClient')];
                    case 4:
                        getMindMap = (_a.sent()).getMindMap;
                        return [4 /*yield*/, getMindMap(latestMindMap.id)];
                    case 5:
                        mindMapData_1 = _a.sent();
                        if (mindMapData_1.nodes && mindMapData_1.nodes.length > 0) {
                            console.log('Loading mind map from database:', mindMapData_1);
                            setMindMapData(mindMapData_1);
                            dbSpreadFactor = mindMapData_1.spread_factor || 1.0;
                            // Handle migration from old spreadFactor values (which were in pixel range ~300)
                            // to new normalized values (0.5-3.0 range)
                            if (dbSpreadFactor > 10) {
                                // Convert old pixel-based values to normalized values
                                dbSpreadFactor = Math.max(0.5, Math.min(3.0, dbSpreadFactor / 300));
                                console.log("Converted legacy spread factor from ".concat(mindMapData_1.spread_factor, " to ").concat(dbSpreadFactor));
                            }
                            dbColorTheme = mindMapData_1.color_theme || colorTheme;
                            setSpreadFactor(dbSpreadFactor);
                            setColorTheme(dbColorTheme);
                            processedNodes = convertDatabaseNodesToMindMapNodes(mindMapData_1.nodes, mindMapData_1.connections || []);
                            hasCustomPositions = processedNodes.some(function (node) {
                                return node.x !== null && node.x !== undefined &&
                                    node.y !== null && node.y !== undefined;
                            });
                            console.log('Database position analysis:', {
                                totalNodes: processedNodes.length,
                                hasCustomPositions: hasCustomPositions,
                                nodePositions: processedNodes.map(function (node) { return ({
                                    id: node.id,
                                    text: node.text,
                                    x: node.x,
                                    y: node.y,
                                    hasPosition: node.x !== null && node.x !== undefined && node.y !== null && node.y !== undefined
                                }); })
                            });
                            finalNodes = void 0;
                            if (hasCustomPositions) {
                                // Use saved positions from database - user has manually positioned nodes
                                console.log('Using saved positions from database');
                                finalNodes = processedNodes.map(function (node) { return (__assign(__assign({}, node), { x: node.x || 0, y: node.y || 0 })); });
                            }
                            else {
                                // Apply hierarchical layout for new mind maps without saved positions
                                console.log('Applying hierarchical layout for new mind map without saved positions');
                                finalNodes = applyHierarchicalLayout(__spreadArray([], processedNodes, true), { x: 500, y: 400 }, dbSpreadFactor);
                            }
                            reactFlowNodes = convertToReactFlowNodes(finalNodes);
                            reactFlowEdges = convertToReactFlowEdges(finalNodes, mindMapData_1.connections);
                            console.log('Processed data with consistent layout:', {
                                processedNodes: processedNodes,
                                finalNodes: finalNodes,
                                reactFlowNodes: reactFlowNodes,
                                reactFlowEdges: reactFlowEdges,
                                dbSpreadFactor: dbSpreadFactor,
                                dbColorTheme: dbColorTheme
                            });
                            setNodes(reactFlowNodes);
                            setEdges(reactFlowEdges);
                        }
                        return [3 /*break*/, 7];
                    case 6:
                        if (generatedContent === null || generatedContent === void 0 ? void 0 : generatedContent.mindmap) {
                            loadFromGeneratedContent();
                        }
                        _a.label = 7;
                    case 7: return [3 /*break*/, 10];
                    case 8:
                        error_12 = _a.sent();
                        console.error('Failed to load existing mind maps:', error_12);
                        if (generatedContent === null || generatedContent === void 0 ? void 0 : generatedContent.mindmap) {
                            loadFromGeneratedContent();
                        }
                        return [3 /*break*/, 10];
                    case 9:
                        setIsLoading(false);
                        return [7 /*endfinally*/];
                    case 10: return [2 /*return*/];
                }
            });
        }); };
        var loadFromGeneratedContent = function () {
            if (generatedContent === null || generatedContent === void 0 ? void 0 : generatedContent.mindmap) {
                console.log('Loading from generated content:', generatedContent.mindmap);
                setMindMapData(generatedContent.mindmap);
                if (generatedContent.mindmap.nodes) {
                    var processedNodes = generatedContent.mindmap.nodes.map(function (node) { return (__assign(__assign({}, node), { isExpanded: node.isExpanded === true || node.isExpanded === 'True', isBookmarked: node.isBookmarked === true || node.isBookmarked === 'True', color: node.color || getNodeColor(node.type, node.level) })); });
                    // Apply consistent hierarchical layout for generated content
                    console.log('Applying consistent hierarchical layout for generated content');
                    var arrangedNodes = applyHierarchicalLayout(__spreadArray([], processedNodes, true), { x: 500, y: 400 }, spreadFactor);
                    var reactFlowNodes = convertToReactFlowNodes(arrangedNodes);
                    var reactFlowEdges = convertToReactFlowEdges(arrangedNodes);
                    console.log('Generated content processed with consistent layout:', {
                        processedNodes: processedNodes,
                        arrangedNodes: arrangedNodes,
                        reactFlowNodes: reactFlowNodes,
                        reactFlowEdges: reactFlowEdges
                    });
                    setNodes(reactFlowNodes);
                    setEdges(reactFlowEdges);
                    // Save hierarchical coordinates to database if we have a sessionId and mind map ID
                    if (sessionId && generatedContent.mindmap.id) {
                        saveHierarchicalCoordinatesToDatabase(arrangedNodes, generatedContent.mindmap.id);
                    }
                }
            }
        };
        loadMindMaps();
    }, [sessionId, generatedContent]); // Removed function dependencies that cause infinite loop
    // Convert database nodes to mind map nodes
    var convertDatabaseNodesToMindMapNodes = function (dbNodes, connections) {
        console.log('Converting database nodes:', {
            dbNodes: dbNodes.length,
            connections: connections.length,
            allConnections: connections
        });
        return dbNodes.map(function (dbNode) {
            // Find all connections where this node is the source
            var nodeConnections = connections
                .filter(function (conn) { return conn.source_node_id === dbNode.id; })
                .map(function (conn) {
                // Find the target node's external_id or id
                var targetNode = dbNodes.find(function (n) { return n.id === conn.target_node_id; });
                return (targetNode === null || targetNode === void 0 ? void 0 : targetNode.external_id) || (targetNode === null || targetNode === void 0 ? void 0 : targetNode.id) || conn.target_node_id;
            })
                .filter(Boolean);
            console.log("Node ".concat(dbNode.id, " (").concat(dbNode.external_id || 'no external_id', ") connections:"), nodeConnections);
            return {
                id: dbNode.external_id || dbNode.id,
                text: dbNode.text,
                x: dbNode.x_position,
                y: dbNode.y_position,
                type: dbNode.node_type,
                color: dbNode.color,
                level: dbNode.level,
                isExpanded: dbNode.is_expanded,
                description: dbNode.description,
                document_id: Array.isArray(dbNode.source_documents)
                    ? dbNode.source_documents[0]
                    : dbNode.source_documents,
                progress: dbNode.progress,
                isBookmarked: dbNode.is_bookmarked,
                dbId: dbNode.id,
                width: dbNode.width,
                height: dbNode.height,
                connections: nodeConnections, // Now properly populated from database connections
            };
        });
    };
    // Utility functions for mind map management
    var exportMindMap = function () {
        // Implementation for exporting mindmap as image/PDF
        console.log('Exporting mindmap...');
        showSuccess('Export feature coming soon!');
    };
    var resetView = function () {
        // Reset React Flow view to fit all nodes
        var reactFlowInstance = reactFlowWrapper.current;
        if (reactFlowInstance) {
            // Note: In actual implementation, you'd use the useReactFlow hook
            console.log('Resetting view...');
        }
        setSelectedNode(null);
        setSearchQuery('');
        showSuccess('View reset');
    };
    // Save entire mind map state to backend
    var saveMindMapState = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var updateMindMap, updatePromises, error_13;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!(mindMapData === null || mindMapData === void 0 ? void 0 : mindMapData.id) || !sessionId) {
                        showError('Cannot save: No mind map or session');
                        return [2 /*return*/];
                    }
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 5, , 6]);
                    return [4 /*yield*/, import('../../../services/apiClient')];
                case 2:
                    updateMindMap = (_a.sent()).updateMindMap;
                    return [4 /*yield*/, updateMindMap(mindMapData.id, {
                            spread_factor: spreadFactor,
                            color_theme: colorTheme,
                            zoom_level: 1,
                            pan_offset_x: 0,
                            pan_offset_y: 0, // You can get this from React Flow instance
                        })];
                case 3:
                    _a.sent();
                    updatePromises = nodes.map(function (node) { return __awaiter(void 0, void 0, void 0, function () {
                        var nodeData, updateMindMapNode;
                        return __generator(this, function (_a) {
                            switch (_a.label) {
                                case 0:
                                    nodeData = node.data;
                                    if (!nodeData.dbId) return [3 /*break*/, 2];
                                    return [4 /*yield*/, import('../../../services/apiClient')];
                                case 1:
                                    updateMindMapNode = (_a.sent()).updateMindMapNode;
                                    return [2 /*return*/, updateMindMapNode(nodeData.dbId, {
                                            x_position: node.position.x,
                                            y_position: node.position.y,
                                            text: nodeData.text,
                                            color: nodeData.color,
                                            is_expanded: nodeData.isExpanded,
                                            is_bookmarked: nodeData.isBookmarked,
                                        })];
                                case 2: return [2 /*return*/];
                            }
                        });
                    }); });
                    return [4 /*yield*/, Promise.all(updatePromises.filter(Boolean))];
                case 4:
                    _a.sent();
                    showSuccess('Mind map saved successfully');
                    return [3 /*break*/, 6];
                case 5:
                    error_13 = _a.sent();
                    console.error('Failed to save mind map state:', error_13);
                    showError('Failed to save mind map');
                    return [3 /*break*/, 6];
                case 6: return [2 /*return*/];
            }
        });
    }); }, [mindMapData, sessionId, spreadFactor, colorTheme, nodes, showSuccess, showError]);
    // Auto-save functionality
    useEffect(function () {
        if (nodes.length > 0 && (mindMapData === null || mindMapData === void 0 ? void 0 : mindMapData.id)) {
            var autoSaveTimer_1 = setTimeout(function () {
                saveMindMapState();
            }, 10000); // Auto-save every 10 seconds (increased from 5)
            return function () { return clearTimeout(autoSaveTimer_1); };
        }
    }, [colorTheme, spreadFactor, mindMapData === null || mindMapData === void 0 ? void 0 : mindMapData.id]); // Removed nodes, edges, and saveMindMapState to prevent infinite loop
    var toggleBookmark = function (nodeId) { return __awaiter(void 0, void 0, void 0, function () {
        var updatedNodes, updatedNode, nodeData, error_14;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    updatedNodes = nodes.map(function (node) {
                        if (node.id === nodeId && node.data) {
                            return __assign(__assign({}, node), { data: __assign(__assign({}, node.data), { isBookmarked: !node.data.isBookmarked }) });
                        }
                        return node;
                    });
                    setNodes(updatedNodes);
                    updatedNode = updatedNodes.find(function (n) { return n.id === nodeId; });
                    nodeData = updatedNode === null || updatedNode === void 0 ? void 0 : updatedNode.data;
                    if (!(nodeData === null || nodeData === void 0 ? void 0 : nodeData.dbId)) return [3 /*break*/, 4];
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, progressTracking.updateNodeProgress(nodeData.dbId, {
                            is_bookmarked: nodeData.isBookmarked
                        })];
                case 2:
                    _a.sent();
                    showSuccess('Bookmark updated successfully');
                    return [3 /*break*/, 4];
                case 3:
                    error_14 = _a.sent();
                    console.error('Failed to update bookmark:', error_14);
                    showError('Failed to update bookmark');
                    return [3 /*break*/, 4];
                case 4:
                    showSuccess('Bookmark toggled');
                    return [2 /*return*/];
            }
        });
    }); };
    var handleEditSubmit = function () {
        // Editing disabled - this function is now a no-op
        console.log('Editing has been disabled');
    };
    // Save hierarchical coordinates to database after mind map generation
    var saveHierarchicalCoordinatesToDatabase = function (arrangedNodes, mindMapId) { return __awaiter(void 0, void 0, void 0, function () {
        var _a, updateMindMapNode_1, getMindMap, latestMindMapData, nodeIdMapping_1, updatePromises, results, error_15;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _b.trys.push([0, 4, , 5]);
                    console.log('Saving hierarchical coordinates to database:', {
                        mindMapId: mindMapId,
                        nodeCount: arrangedNodes.length,
                        samplePositions: arrangedNodes.slice(0, 3).map(function (n) { return ({ id: n.id, x: n.x, y: n.y, dbId: n.dbId }); })
                    });
                    return [4 /*yield*/, import('../../../services/apiClient')];
                case 1:
                    _a = _b.sent(), updateMindMapNode_1 = _a.updateMindMapNode, getMindMap = _a.getMindMap;
                    return [4 /*yield*/, getMindMap(mindMapId)];
                case 2:
                    latestMindMapData = _b.sent();
                    nodeIdMapping_1 = new Map();
                    latestMindMapData.nodes.forEach(function (dbNode) {
                        if (dbNode.external_id) {
                            nodeIdMapping_1.set(dbNode.external_id, dbNode.id);
                        }
                    });
                    updatePromises = arrangedNodes.map(function (node) { return __awaiter(void 0, void 0, void 0, function () {
                        var dbId;
                        return __generator(this, function (_a) {
                            dbId = node.dbId || nodeIdMapping_1.get(node.id);
                            if (dbId) {
                                console.log("Updating position for node ".concat(node.id, " (dbId: ").concat(dbId, ") to (").concat(node.x, ", ").concat(node.y, ")"));
                                return [2 /*return*/, updateMindMapNode_1(dbId, {
                                        x_position: Math.round(node.x || 0),
                                        y_position: Math.round(node.y || 0)
                                    })];
                            }
                            else {
                                console.warn("No database ID found for node ".concat(node.id, ", skipping position update"));
                            }
                            return [2 /*return*/];
                        });
                    }); });
                    return [4 /*yield*/, Promise.all(updatePromises.filter(Boolean))];
                case 3:
                    results = _b.sent();
                    console.log("Successfully saved hierarchical coordinates to database for ".concat(results.length, " nodes"));
                    return [3 /*break*/, 5];
                case 4:
                    error_15 = _b.sent();
                    console.error('Failed to save hierarchical coordinates to database:', error_15);
                    return [3 /*break*/, 5];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    // Generate mind map from documents with confirmation for regeneration
    var handleGenerateMindMap = function () { return __awaiter(void 0, void 0, void 0, function () {
        var readyDocuments;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!documents || documents.length === 0) {
                        showError('No documents available to generate mind map');
                        return [2 /*return*/];
                    }
                    readyDocuments = documents.filter(function (doc) { return doc.status === 'ready'; });
                    if (readyDocuments.length === 0) {
                        showError('No processed documents available. Please wait for document processing to complete.');
                        return [2 /*return*/];
                    }
                    // If mind map already exists, show confirmation
                    if (nodes.length > 0) {
                        setShowRegenerateConfirm(true);
                        return [2 /*return*/];
                    }
                    return [4 /*yield*/, generateMindMap()];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); };
    var generateMindMap = function () { return __awaiter(void 0, void 0, void 0, function () {
        var readyDocuments, documentIds_1, result, getMindMap, mindMapData_2, processedNodes, arrangedNodes, reactFlowNodes, reactFlowEdges, dbError_1, mindMapWithId, processedNodes, arrangedNodes, reactFlowNodes, reactFlowEdges, error_16;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setShowRegenerateConfirm(false);
                    setIsGenerating(true);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 13, 14, 15]);
                    readyDocuments = documents.filter(function (doc) { return doc.status === 'ready'; });
                    documentIds_1 = readyDocuments.map(function (doc) { return doc.id; });
                    return [4 /*yield*/, import('../../../services/apiClient').then(function (api) { return api.generateMindMap(sessionId, documentIds_1); })];
                case 2:
                    result = _a.sent();
                    if (!(result === null || result === void 0 ? void 0 : result.mindmap)) return [3 /*break*/, 12];
                    console.log('Mind map generation result:', result);
                    if (!result.mind_map_id) return [3 /*break*/, 9];
                    _a.label = 3;
                case 3:
                    _a.trys.push([3, 8, , 9]);
                    return [4 /*yield*/, import('../../../services/apiClient')];
                case 4:
                    getMindMap = (_a.sent()).getMindMap;
                    return [4 /*yield*/, getMindMap(result.mind_map_id)];
                case 5:
                    mindMapData_2 = _a.sent();
                    if (!(mindMapData_2.nodes && mindMapData_2.nodes.length > 0)) return [3 /*break*/, 7];
                    console.log('Loading mind map from generated content fallback:', mindMapData_2);
                    setMindMapData(mindMapData_2);
                    processedNodes = convertDatabaseNodesToMindMapNodes(mindMapData_2.nodes, mindMapData_2.connections || []);
                    arrangedNodes = applyHierarchicalLayout(__spreadArray([], processedNodes, true), { x: 500, y: 400 }, spreadFactor);
                    console.log('Applied hierarchical layout to generated nodes:', arrangedNodes);
                    reactFlowNodes = convertToReactFlowNodes(arrangedNodes);
                    reactFlowEdges = convertToReactFlowEdges(arrangedNodes, mindMapData_2.connections);
                    console.log('Processed fallback data with hierarchical layout:', {
                        processedNodes: processedNodes,
                        arrangedNodes: arrangedNodes,
                        reactFlowNodes: reactFlowNodes,
                        reactFlowEdges: reactFlowEdges
                    });
                    setNodes(reactFlowNodes);
                    setEdges(reactFlowEdges);
                    // Save the hierarchical coordinates to the database
                    return [4 /*yield*/, saveHierarchicalCoordinatesToDatabase(arrangedNodes, mindMapData_2.id)];
                case 6:
                    // Save the hierarchical coordinates to the database
                    _a.sent();
                    showSuccess('Mind map generated and loaded from database with hierarchical layout!');
                    return [2 /*return*/];
                case 7: return [3 /*break*/, 9];
                case 8:
                    dbError_1 = _a.sent();
                    console.error('Failed to load from database, using generated data:', dbError_1);
                    return [3 /*break*/, 9];
                case 9:
                    mindMapWithId = __assign(__assign({}, result.mindmap), { id: result.mind_map_id });
                    setMindMapData(mindMapWithId);
                    if (!result.mindmap.nodes) return [3 /*break*/, 11];
                    processedNodes = result.mindmap.nodes.map(function (node) { return (__assign(__assign({}, node), { isExpanded: node.isExpanded === true || node.isExpanded === 'True', isBookmarked: node.isBookmarked === true || node.isBookmarked === 'True', color: node.color || getNodeColor(node.type, node.level) })); });
                    arrangedNodes = applyHierarchicalLayout(__spreadArray([], processedNodes, true), { x: 500, y: 400 }, spreadFactor);
                    console.log('Applied hierarchical layout to generated fallback nodes:', arrangedNodes);
                    reactFlowNodes = convertToReactFlowNodes(arrangedNodes);
                    reactFlowEdges = convertToReactFlowEdges(arrangedNodes);
                    setNodes(reactFlowNodes);
                    setEdges(reactFlowEdges);
                    if (!result.mind_map_id) return [3 /*break*/, 11];
                    return [4 /*yield*/, saveHierarchicalCoordinatesToDatabase(arrangedNodes, result.mind_map_id)];
                case 10:
                    _a.sent();
                    _a.label = 11;
                case 11:
                    showSuccess('Mind map generated successfully!');
                    _a.label = 12;
                case 12: return [3 /*break*/, 15];
                case 13:
                    error_16 = _a.sent();
                    console.error('Error generating mind map:', error_16);
                    showError('Failed to generate mind map. Please try again.');
                    return [3 /*break*/, 15];
                case 14:
                    setIsGenerating(false);
                    return [7 /*endfinally*/];
                case 15: return [2 /*return*/];
            }
        });
    }); };
    // Handle viewport changes for mobile responsiveness
    useEffect(function () {
        var handleResize = function () {
            setViewportWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return function () { return window.removeEventListener('resize', handleResize); };
    }, []);
    // Enhanced keyboard shortcuts with better navigation
    useEffect(function () {
        var handleKeyPress = function (e) {
            var _a;
            // Don't handle shortcuts when editing
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
                return;
            }
            var selectedNodeData = selectedNode ? nodes.find(function (n) { return n.id === selectedNode; }) : null;
            switch (e.key.toLowerCase()) {
                case 'escape':
                    setSelectedNode(null);
                    setSearchQuery('');
                    break;
                case 'enter':
                    // Editing disabled
                    console.log('Node editing has been disabled');
                    break;
                case 'delete':
                case 'backspace':
                    if (selectedNode && ((_a = selectedNodeData === null || selectedNodeData === void 0 ? void 0 : selectedNodeData.data) === null || _a === void 0 ? void 0 : _a.type) !== 'main') {
                        handleNodeDelete(selectedNode);
                    }
                    break;
                case '+':
                case '=':
                    if (selectedNode) {
                        handleNodeAddChild(selectedNode);
                    }
                    break;
                case 'b':
                    if (selectedNode) {
                        toggleBookmark(selectedNode);
                    }
                    break;
                case 'r':
                    resetView();
                    break;
                case 'f':
                    if (searchInputRef.current) {
                        searchInputRef.current.focus();
                    }
                    break;
                case ' ':
                    e.preventDefault();
                    if (selectedNode && selectedNodeData) {
                        handleNodeToggleExpansion(selectedNode);
                    }
                    break;
                case 'arrowup':
                case 'arrowdown':
                case 'arrowleft':
                case 'arrowright':
                    e.preventDefault();
                    if (selectedNode && nodes.length > 1) {
                        // Navigate to connected nodes or next/previous node
                        var currentIndex = nodes.findIndex(function (n) { return n.id === selectedNode; });
                        var nextIndex = void 0;
                        if (e.key === 'arrowdown' || e.key === 'arrowright') {
                            nextIndex = (currentIndex + 1) % nodes.length;
                        }
                        else {
                            nextIndex = currentIndex === 0 ? nodes.length - 1 : currentIndex - 1;
                        }
                        setSelectedNode(nodes[nextIndex].id);
                    }
                    else if (!selectedNode && nodes.length > 0) {
                        // Select first main node or first node
                        var mainNode = nodes.find(function (n) { var _a; return ((_a = n.data) === null || _a === void 0 ? void 0 : _a.type) === 'main'; });
                        setSelectedNode((mainNode === null || mainNode === void 0 ? void 0 : mainNode.id) || nodes[0].id);
                    }
                    break;
            }
        };
        window.addEventListener('keydown', handleKeyPress);
        return function () { return window.removeEventListener('keydown', handleKeyPress); };
    }, [selectedNode, nodes, handleNodeDelete, handleNodeAddChild, handleNodeToggleExpansion, toggleBookmark, resetView]);
    // Auto-save mind map changes
    var saveMindMapToBackend = function (updatedNodes, updatedSettings) { return __awaiter(void 0, void 0, void 0, function () {
        var _a, updateMindMap, updateMindMapNode_2, savePromises, error_17;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!sessionId || !mindMapData)
                        return [2 /*return*/];
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 7, , 8]);
                    return [4 /*yield*/, import('../../../services/apiClient')];
                case 2:
                    _a = _b.sent(), updateMindMap = _a.updateMindMap, updateMindMapNode_2 = _a.updateMindMapNode;
                    if (!(updatedSettings && mindMapData.id)) return [3 /*break*/, 4];
                    return [4 /*yield*/, updateMindMap(mindMapData.id, {
                            color_theme: colorTheme,
                            spread_factor: spreadFactor,
                        })];
                case 3:
                    _b.sent();
                    _b.label = 4;
                case 4:
                    if (!updatedNodes) return [3 /*break*/, 6];
                    savePromises = updatedNodes.map(function (node) { return __awaiter(void 0, void 0, void 0, function () {
                        var _a;
                        return __generator(this, function (_b) {
                            if ((_a = node.data) === null || _a === void 0 ? void 0 : _a.dbId) {
                                return [2 /*return*/, updateMindMapNode_2(node.data.dbId, {
                                        x_position: node.position.x,
                                        y_position: node.position.y,
                                        is_expanded: node.data.isExpanded,
                                        is_bookmarked: node.data.isBookmarked,
                                    })];
                            }
                            return [2 /*return*/];
                        });
                    }); });
                    return [4 /*yield*/, Promise.all(savePromises.filter(Boolean))];
                case 5:
                    _b.sent();
                    _b.label = 6;
                case 6: return [3 /*break*/, 8];
                case 7:
                    error_17 = _b.sent();
                    console.error('Failed to save mind map changes:', error_17);
                    return [3 /*break*/, 8];
                case 8: return [2 /*return*/];
            }
        });
    }); };
    // Load progress data when nodes change
    useEffect(function () {
        var loadNodeProgress = function () { return __awaiter(void 0, void 0, void 0, function () {
            var nodeIds, progressDataArray_1, error_18;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!nodes.length)
                            return [2 /*return*/];
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        nodeIds = nodes
                            .map(function (node) { var _a; return (_a = node.data) === null || _a === void 0 ? void 0 : _a.dbId; })
                            .filter(function (id) { return id !== undefined && id !== null; });
                        return [4 /*yield*/, progressTracking.getMultipleNodeProgress(nodeIds)];
                    case 2:
                        progressDataArray_1 = _a.sent();
                        // Update nodes with progress data
                        if (progressDataArray_1 && progressDataArray_1.length > 0) {
                            setNodes(function (prev) { return prev.map(function (node) {
                                var nodeData = node.data;
                                var progressData = progressDataArray_1.find(function (p) { return p.node_id === nodeData.dbId; });
                                if (progressData) {
                                    var updatedData = __assign({}, nodeData);
                                    updatedData.progress = progressData.progress.overall_progress || 0;
                                    updatedData.isBookmarked = progressData.progress.is_bookmarked || false;
                                    return __assign(__assign({}, node), { data: updatedData });
                                }
                                return node;
                            }); });
                        }
                        return [3 /*break*/, 4];
                    case 3:
                        error_18 = _a.sent();
                        console.error('Failed to load node progress:', error_18);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        }); };
        // Debounce the progress loading to avoid too many API calls
        var timeoutId = setTimeout(loadNodeProgress, 500);
        return function () { return clearTimeout(timeoutId); };
    }, [nodes.length, progressTracking, setNodes]); // Only trigger when nodes count changes
    // Reflow nodes when layout settings change
    useEffect(function () {
        if (nodes.length > 0) {
            var mindMapNodes = nodes.map(function (node) {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
                return ({
                    id: node.id,
                    text: ((_a = node.data) === null || _a === void 0 ? void 0 : _a.text) || '',
                    x: node.position.x,
                    y: node.position.y,
                    type: ((_b = node.data) === null || _b === void 0 ? void 0 : _b.type) || 'leaf',
                    color: ((_c = node.data) === null || _c === void 0 ? void 0 : _c.color) || '#8B5CF6',
                    connections: [],
                    level: ((_d = node.data) === null || _d === void 0 ? void 0 : _d.level) || 0,
                    isExpanded: (_e = node.data) === null || _e === void 0 ? void 0 : _e.isExpanded,
                    description: (_f = node.data) === null || _f === void 0 ? void 0 : _f.description,
                    document_id: (_g = node.data) === null || _g === void 0 ? void 0 : _g.document_id,
                    resources: (_h = node.data) === null || _h === void 0 ? void 0 : _h.resources,
                    progress: (_j = node.data) === null || _j === void 0 ? void 0 : _j.progress,
                    lastVisited: (_k = node.data) === null || _k === void 0 ? void 0 : _k.lastVisited,
                    isBookmarked: (_l = node.data) === null || _l === void 0 ? void 0 : _l.isBookmarked,
                    dbId: (_m = node.data) === null || _m === void 0 ? void 0 : _m.dbId,
                });
            });
            var arrangedNodes = applyLayout(mindMapNodes);
            var reactFlowNodes = convertToReactFlowNodes(arrangedNodes);
            // Only update if positions actually changed to prevent infinite loop
            var positionsChanged = reactFlowNodes.some(function (newNode, index) {
                var currentNode = nodes[index];
                return !currentNode ||
                    Math.abs(newNode.position.x - currentNode.position.x) > 1 ||
                    Math.abs(newNode.position.y - currentNode.position.y) > 1;
            });
            if (positionsChanged) {
                setNodes(reactFlowNodes);
                // Auto-save layout changes
                saveMindMapToBackend(reactFlowNodes, { colorTheme: colorTheme, spreadFactor: spreadFactor });
            }
        }
    }, [spreadFactor, colorTheme]); // Deliberately NOT including nodes to prevent infinite loop
    // Load progress data for a node
    var loadNodeProgress = useCallback(function (nodeId) { return __awaiter(void 0, void 0, void 0, function () {
        var node, nodeData, dbId, progressData, error_19;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    node = nodes.find(function (n) { return n.id === nodeId; });
                    nodeData = node === null || node === void 0 ? void 0 : node.data;
                    dbId = nodeData === null || nodeData === void 0 ? void 0 : nodeData.dbId;
                    if (!dbId) {
                        console.error('Node database ID not found for node:', nodeId);
                        return [2 /*return*/];
                    }
                    return [4 /*yield*/, progressTracking.getNodeProgress(dbId)];
                case 1:
                    progressData = _a.sent();
                    setNodeProgressData(progressData);
                    return [3 /*break*/, 3];
                case 2:
                    error_19 = _a.sent();
                    console.error('Failed to fetch node progress:', error_19);
                    // Show default progress data if fetch fails
                    setNodeProgressData({
                        overall_progress: 0,
                        mastery_level: 'exploring',
                        quiz_progress: 0,
                        flashcard_progress: 0,
                        reading_progress: 0,
                        total_study_sessions: 0,
                        total_study_time_minutes: 0,
                        is_bookmarked: false,
                        priority_level: 3,
                        current_study_streak_days: 0,
                        longest_study_streak_days: 0,
                    });
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); }, [progressTracking]);
    // Handle node selection
    var onNodeClick = useCallback(function (_, node) { return __awaiter(void 0, void 0, void 0, function () {
        var nodeId;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    nodeId = node.id;
                    if (!(selectedNode === nodeId)) return [3 /*break*/, 1];
                    // If clicking the same node, toggle controls panel
                    setShowMobileSidebar(!showMobileSidebar);
                    return [3 /*break*/, 3];
                case 1:
                    // Select new node, load progress data, and open controls panel
                    setSelectedNode(nodeId);
                    return [4 /*yield*/, loadNodeProgress(nodeId)];
                case 2:
                    _a.sent();
                    setShowMobileSidebar(true);
                    _a.label = 3;
                case 3: return [2 /*return*/];
            }
        });
    }); }, [selectedNode, showMobileSidebar, loadNodeProgress]);
    // Handle connection
    var onConnect = useCallback(function (params) { return __awaiter(void 0, void 0, void 0, function () {
        var newEdge, sourceNode, targetNode, sourceData, targetData, createMindMapConnection, error_20;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    newEdge = __assign(__assign({}, params), { id: "".concat(params.source, "-").concat(params.target), type: 'mindMapEdge', data: {
                            connectionType: 'custom',
                            isBidirectional: false,
                            color: '#8B5CF6',
                        }, markerEnd: {
                            type: MarkerType.ArrowClosed,
                            color: '#8B5CF6',
                        } });
                    setEdges(function (eds) { return addEdge(newEdge, eds); });
                    if (!((mindMapData === null || mindMapData === void 0 ? void 0 : mindMapData.id) && params.source && params.target)) return [3 /*break*/, 6];
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 5, , 6]);
                    sourceNode = nodes.find(function (n) { return n.id === params.source; });
                    targetNode = nodes.find(function (n) { return n.id === params.target; });
                    sourceData = sourceNode === null || sourceNode === void 0 ? void 0 : sourceNode.data;
                    targetData = targetNode === null || targetNode === void 0 ? void 0 : targetNode.data;
                    if (!((sourceData === null || sourceData === void 0 ? void 0 : sourceData.dbId) && (targetData === null || targetData === void 0 ? void 0 : targetData.dbId))) return [3 /*break*/, 4];
                    return [4 /*yield*/, import('../../../services/apiClient')];
                case 2:
                    createMindMapConnection = (_a.sent()).createMindMapConnection;
                    return [4 /*yield*/, createMindMapConnection(mindMapData.id, {
                            source_node_id: sourceData.dbId,
                            target_node_id: targetData.dbId,
                            connection_type: 'custom',
                            stroke_color: '#8B5CF6',
                            stroke_width: 2,
                        })];
                case 3:
                    _a.sent();
                    showSuccess('Connection created successfully');
                    _a.label = 4;
                case 4: return [3 /*break*/, 6];
                case 5:
                    error_20 = _a.sent();
                    console.error('Failed to create connection in backend:', error_20);
                    showError('Failed to save connection');
                    return [3 /*break*/, 6];
                case 6: return [2 /*return*/];
            }
        });
    }); }, [setEdges, mindMapData, nodes, showSuccess, showError]);
    // Debug effect to monitor state changes
    useEffect(function () {
        console.log('Nodes state changed:', nodes);
    }, [nodes]);
    useEffect(function () {
        console.log('Edges state changed:', edges);
    }, [edges]);
    // Optimize edge highlighting for search without recreating edges (throttled)
    useEffect(function () {
        var timeoutId = setTimeout(function () {
            if (edges.length > 0) {
                var hasSearchQuery_1 = searchQuery.trim().length > 0;
                setEdges(function (currentEdges) {
                    return currentEdges.map(function (edge) {
                        var _a, _b, _c, _d, _e;
                        var sourceNode = nodes.find(function (n) { return n.id === edge.source; });
                        var targetNode = nodes.find(function (n) { return n.id === edge.target; });
                        if (!sourceNode || !targetNode)
                            return edge;
                        var shouldHighlight = hasSearchQuery_1 && (((_b = (_a = sourceNode.data) === null || _a === void 0 ? void 0 : _a.text) === null || _b === void 0 ? void 0 : _b.toLowerCase().includes(searchQuery.toLowerCase())) ||
                            ((_d = (_c = targetNode.data) === null || _c === void 0 ? void 0 : _c.text) === null || _d === void 0 ? void 0 : _d.toLowerCase().includes(searchQuery.toLowerCase())));
                        // Only update if highlighting state actually changed
                        if (Boolean((_e = edge.data) === null || _e === void 0 ? void 0 : _e.isSearchHighlight) !== shouldHighlight) {
                            return __assign(__assign({}, edge), { data: __assign(__assign({}, edge.data), { isSearchHighlight: shouldHighlight }) });
                        }
                        return edge;
                    });
                });
            }
        }, 150); // Throttle updates to prevent too many re-renders
        return function () { return clearTimeout(timeoutId); };
    }, [searchQuery, nodes, setEdges]); // Only depend on searchQuery and nodes, not edges to prevent loops
    return (_jsxs("div", __assign({ className: "h-full w-full flex flex-col overflow-hidden" }, { children: [_jsx(MindMapControls, { searchQuery: searchQuery, setSearchQuery: setSearchQuery, searchInputRef: searchInputRef, nodes: nodes, edges: edges, selectedNode: selectedNode, setSelectedNode: setSelectedNode, setNodes: setNodes, isGenerating: isGenerating, handleGenerateMindMap: handleGenerateMindMap, handleAddRootNode: handleAddRootNode, resetView: resetView, saveMindMapState: saveMindMapState, exportMindMap: exportMindMap, handleNodeAddChild: handleNodeAddChild, handleNodeDelete: handleNodeDelete, handleNodeColorChange: handleNodeColorChange, toggleBookmark: toggleBookmark, spreadFactor: spreadFactor, setSpreadFactor: setSpreadFactor, colorTheme: colorTheme, setColorTheme: setColorTheme, applyLayout: applyLayout, convertToReactFlowNodes: convertToReactFlowNodes, showHelp: showHelp, setShowHelp: setShowHelp, showMobileSidebar: showMobileSidebar, setShowMobileSidebar: setShowMobileSidebar, controlsOpen: controlsOpen, setControlsOpen: setControlsOpen, handleGenerateQuiz: handleGenerateQuiz, handleGenerateFlashcards: handleGenerateFlashcards, handleUpdateProgress: handleUpdateProgress, handleCreateGoal: handleCreateGoal, mindMapData: mindMapData, documents: documents, viewportWidth: viewportWidth, colorThemes: colorThemes }), _jsx("div", __assign({ className: "flex-1 min-h-0 relative flex" }, { children: _jsx("div", __assign({ className: "w-full" }, { children: _jsx("div", __assign({ ref: reactFlowWrapper, className: "h-full w-full touch-pan-x touch-pan-y overscroll-contain" }, { children: _jsxs(ReactFlow, __assign({ nodes: nodes, edges: edges, onNodesChange: handleNodesChange, onEdgesChange: onEdgesChange, onConnect: onConnect, onNodeClick: onNodeClick, nodeTypes: nodeTypes, edgeTypes: edgeTypes, fitView: true, fitViewOptions: {
                                padding: 0.1,
                                minZoom: 0.1,
                                maxZoom: 2,
                            }, minZoom: 0.1, maxZoom: 2, attributionPosition: "bottom-left", className: "bg-background touch-pan-y", panOnScroll: viewportWidth < 640, panOnScrollMode: viewportWidth < 640 ? PanOnScrollMode.Vertical : undefined, zoomOnScroll: viewportWidth >= 640, zoomOnPinch: true, panOnDrag: true, nodesDraggable: true, selectNodesOnDrag: false, multiSelectionKeyCode: null, deleteKeyCode: null, connectionMode: 'loose', snapToGrid: false, snapGrid: [20, 20], onInit: function () {
                                console.log('ReactFlow initialized');
                                console.log('Nodes:', nodes);
                                console.log('Edges:', edges);
                                console.log('NodeTypes:', nodeTypes);
                                console.log('EdgeTypes:', edgeTypes);
                            } }, { children: [_jsx(Background, { color: "#aaa", gap: 16 }), _jsx(Controls, { position: "bottom-right" })] })) })) })) })), showRegenerateConfirm && (_jsx("div", __assign({ className: "absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50 p-4" }, { children: _jsxs("div", __assign({ className: "bg-card border border-border p-4 sm:p-6 rounded-lg shadow-xl max-w-md w-full" }, { children: [_jsx("h3", __assign({ className: "font-medium mb-3 text-foreground text-sm sm:text-base" }, { children: "Regenerate Mind Map?" })), _jsx("p", __assign({ className: "text-muted-foreground text-sm mb-4" }, { children: "This will replace your current mind map with a new one generated from your documents. Any unsaved changes will be lost." })), _jsxs("div", __assign({ className: "flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3" }, { children: [_jsxs(Button, __assign({ onClick: generateMindMap, className: "flex-1 text-sm", variant: "destructive" }, { children: [_jsx(Brain, { className: "w-4 h-4 mr-2" }), "Yes, Regenerate"] })), _jsx(Button, __assign({ onClick: function () { return setShowRegenerateConfirm(false); }, variant: "outline", className: "flex-1 text-sm" }, { children: "Cancel" }))] }))] })) }))), _jsx(Modal, __assign({ isOpen: showNodeEditDialog, onClose: function () {
                    setShowNodeEditDialog(false);
                    setEditingNode(null);
                    setEditingNodeText('');
                    setEditingNodeDescription('');
                }, title: "Edit Node", description: "Edit the node's title and description", size: "md" }, { children: _jsxs("div", __assign({ className: "space-y-4" }, { children: [_jsxs("div", { children: [_jsx("label", __assign({ htmlFor: "nodeText", className: "block text-sm font-medium text-foreground mb-2" }, { children: "Node Title *" })), _jsx("input", { id: "nodeText", type: "text", value: editingNodeText, onChange: function (e) { return setEditingNodeText(e.target.value); }, placeholder: "Enter node title...", className: "w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring transition-all duration-200", autoFocus: true })] }), _jsxs("div", { children: [_jsx("label", __assign({ htmlFor: "nodeDescription", className: "block text-sm font-medium text-foreground mb-2" }, { children: "Description (Optional)" })), _jsx("textarea", { id: "nodeDescription", value: editingNodeDescription, onChange: function (e) { return setEditingNodeDescription(e.target.value); }, placeholder: "Enter additional details or notes about this concept...", rows: 3, className: "w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring transition-all duration-200 resize-none" })] }), _jsxs("div", __assign({ className: "flex justify-end gap-3 pt-4" }, { children: [_jsx(Button, __assign({ variant: "outline", onClick: function () {
                                        setShowNodeEditDialog(false);
                                        setEditingNode(null);
                                        setEditingNodeText('');
                                        setEditingNodeDescription('');
                                    } }, { children: "Cancel" })), _jsx(Button, __assign({ onClick: function () {
                                        if (editingNode && editingNodeText.trim()) {
                                            handleNodeSave(editingNode, editingNodeText.trim(), editingNodeDescription.trim() || undefined);
                                        }
                                    }, disabled: !editingNodeText.trim() }, { children: "Save Changes" }))] }))] })) }))] })));
};
// Wrap with ReactFlowProvider
var MindMapTabWithProvider = function (props) {
    return (_jsx(ReactFlowProvider, { children: _jsx(MindMapTab, __assign({}, props)) }));
};
export default MindMapTabWithProvider;
